import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { isTabAvailable, resolveCapabilities } from '../features/capabilities';
import { useFeatureStore } from './featureStore';
import { useHelpStore } from '../help/helpStore';

export type TabType = 'landing' | 'network-monitoring' | 'map' | 'grid' | 'settings' | 'documentation' | 'stress' | 'monitoring' | 'switching-history' | 'netzanschluss';

export interface Tab {
  id: string;
  title: string;
  type: TabType;
  closeable: boolean;
}

const TAB_TITLES: Record<TabType, string> = {
  landing: 'Dashboard',
  'network-monitoring': 'Netzmonitoring',
  map: 'Karte',
  grid: 'Tabellenansicht',
  settings: 'Einstellungen',
  documentation: 'Hilfe & Einstieg',
  stress: 'Stressszenarios',
  monitoring: 'Monitoring',
  'switching-history': 'Schalthistorie',
  netzanschluss: 'Netzanschlussprüfung',
};

/**
 * What a tab is about — the ONE classification behind the grouped tab bar, the polling pause
 * and the zoneless overlay:
 * - `fleet`: all zones at once, no zone-scoped data (dashboard, Netzmonitoring)
 * - `zone`: the ONE selected zone (map, monitoring, tables, planning, …)
 * - `app`: neither (settings, documentation)
 * A new tab type must be classified here; nothing else may hard-code tab types for these purposes.
 */
export type TabScope = 'fleet' | 'zone' | 'app';

export const TAB_SCOPES: Record<TabType, TabScope> = {
  landing: 'fleet',
  'network-monitoring': 'fleet',
  map: 'zone',
  monitoring: 'zone',
  grid: 'zone',
  stress: 'zone',
  'switching-history': 'zone',
  netzanschluss: 'zone',
  settings: 'app',
  documentation: 'app',
};

const tabTypesOfScope = (...scopes: TabScope[]): TabType[] =>
  (Object.keys(TAB_SCOPES) as TabType[]).filter(type => scopes.includes(TAB_SCOPES[type]));

/**
 * Tabs that show all zones at once and no zone-scoped data. The load-flow polling of the
 * (hidden) map pauses there and the left zone sidebar is not rendered.
 */
export const CROSS_ZONE_TAB_TYPES: ReadonlySet<TabType> = new Set<TabType>(tabTypesOfScope('fleet'));

/** Tabs that work without a selected zone — no "Keine Zone ausgewählt" overlay. */
export const ZONELESS_TAB_TYPES: ReadonlySet<TabType> = new Set<TabType>(tabTypesOfScope('fleet', 'app'));

export const isCrossZoneTab = (type: TabType | undefined): boolean =>
  type != null && CROSS_ZONE_TAB_TYPES.has(type);

interface TabState {
  tabs: Tab[];
  activeTabId: string;
}

interface TabActions {
  openTab: (type: TabType) => void;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  getActiveTab: () => Tab | undefined;
}

type TabStore = TabState & TabActions;

const ACTIVE_TAB_KEY = 'gridpilot_active_tab';

function getRestoredTabs(): { tabs: Tab[]; activeTabId: string } {
  const savedType = sessionStorage.getItem(ACTIVE_TAB_KEY) as TabType | null;
  const landingTab: Tab = { id: 'dashboard', title: 'Dashboard', type: 'landing', closeable: false };
  const networkMonitoringTab: Tab = { id: 'netzmonitoring', title: TAB_TITLES['network-monitoring'], type: 'network-monitoring', closeable: false };
  const mapTab: Tab = { id: 'karte', title: 'Karte', type: 'map', closeable: false };
  const monitoringTab: Tab = { id: 'monitoring', title: 'Monitoring', type: 'monitoring', closeable: false };
  // Fixed tabs, in display order: all zones (Dashboard, Netzmonitoring), then the selected zone
  const fixedTabs = [landingTab, networkMonitoringTab, mapTab, monitoringTab];
  const fixedTab = fixedTabs.find(tab => tab.type === savedType);

  if (savedType && !fixedTab && savedType in TAB_TITLES) {
    const restoredTab: Tab = {
      id: `${savedType}-restored`,
      title: TAB_TITLES[savedType],
      type: savedType,
      closeable: true,
    };
    return { tabs: [...fixedTabs, restoredTab], activeTabId: restoredTab.id };
  }

  // A fixed tab was active, else the default: the landing page
  return { tabs: fixedTabs, activeTabId: (fixedTab ?? landingTab).id };
}

export const useTabStore = create<TabStore>()(
  devtools((set, get) => ({
    // Initial state: restore previously active tab from sessionStorage
    ...getRestoredTabs(),

    openTab: (type: TabType) => {
      if (!isTabAvailable(type, resolveCapabilities(useFeatureStore.getState()))) return;
      const state = get();

      // Check if tab with this type already exists
      const existingTab = state.tabs.find(tab => tab.type === type);

      if (existingTab) {
        // Activate existing tab
        set({ activeTabId: existingTab.id }, false, 'activateExistingTab');
        sessionStorage.setItem(ACTIVE_TAB_KEY, existingTab.type);
      } else {
        // Create new tab
        const newTab: Tab = {
          id: `${type}-${Date.now()}`,
          title: TAB_TITLES[type],
          type,
          closeable: true, // All new tabs are closeable (except the initial map tab)
        };

        set(
          {
            tabs: [...state.tabs, newTab],
            activeTabId: newTab.id,
          },
          false,
          'openNewTab'
        );
        sessionStorage.setItem(ACTIVE_TAB_KEY, type);
      }
    },

    closeTab: (id: string) => {
      const state = get();
      const tabToClose = state.tabs.find(tab => tab.id === id);

      // Cannot close non-closeable tabs
      if (!tabToClose || !tabToClose.closeable) {
        return;
      }

      const newTabs = state.tabs.filter(tab => tab.id !== id);

      // If closing the active tab, switch to the previous tab or the first available
      let newActiveId = state.activeTabId;
      if (state.activeTabId === id) {
        const closedIndex = state.tabs.findIndex(tab => tab.id === id);
        const prevTab = state.tabs[closedIndex - 1] || newTabs[0];
        newActiveId = prevTab?.id || 'karte';
      }

      set(
        {
          tabs: newTabs,
          activeTabId: newActiveId,
        },
        false,
        'closeTab'
      );
      const activeTab = newTabs.find(t => t.id === newActiveId);
      sessionStorage.setItem(ACTIVE_TAB_KEY, activeTab?.type || 'map');
    },

    setActiveTab: (id: string) => {
      const state = get();
      const target = state.tabs.find(tab => tab.id === id);
      if (target && !isTabAvailable(target.type, resolveCapabilities(useFeatureStore.getState()))) return;
      const tabExists = state.tabs.some(tab => tab.id === id);

      if (tabExists) {
        set({ activeTabId: id }, false, 'setActiveTab');
        const tab = state.tabs.find(t => t.id === id);
        sessionStorage.setItem(ACTIVE_TAB_KEY, tab?.type || 'map');
      }
    },

    getActiveTab: () => {
      const state = get();
      return state.tabs.find(tab => tab.id === state.activeTabId);
    },
  }))
);

// Remove unavailable restored tabs after discovery; App guards rendering before this runs.
useFeatureStore.subscribe(state => {
  if (state.status !== 'ready' && state.status !== 'error') return;
  const capabilities = resolveCapabilities(state);
  for (const tab of useTabStore.getState().tabs) {
    if (!isTabAvailable(tab.type, capabilities)) useTabStore.getState().closeTab(tab.id);
  }
});

useTabStore.subscribe((state, previous) => {
  const type = state.tabs.find(tab => tab.id === state.activeTabId)?.type;
  if (type && type !== 'documentation') {
    useHelpStore.setState({ originTab: type });
    const previousType = previous.tabs.find(tab => tab.id === previous.activeTabId)?.type;
    const url = new URL(window.location.href);
    if (previousType === 'documentation' && url.searchParams.has('help')) {
      url.searchParams.delete('help');
      window.history.pushState({ gridpilotTab: type }, '', url);
    }
  }
});
